import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceGet } from '../../models/datasource';

@Component({
  selector: 'app-data-source-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './data-source-picker.component.html',
  styleUrls: ['./data-source-picker.component.scss']
})
export class DataSourcePickerComponent {
  @Input() dataSources: DataSourceGet[] = [];
  @Input() selectedDataSourceId: string | undefined;
  @Input() selectedDataSourceIds: string[] = [];
  @Input() allowMultipleSelection = false;
  @Input() allowUploadFile = true;
  @Input() allowDataSourceSelection = true;
  @Input() allowCreateNew = true;
  @Input() isLoadingDataSources = false;
  @Input() uploadLabel = 'Select Local File';
  @Input() uploadDescription = 'Temporary one-time upload - data not stored';
  @Input() showInfoIcon = false;
  @Input() checkIcon = 'check_circle';
  @Input() dataSourceIcon = 'data-set';

  @Output() uploadFile = new EventEmitter<void>();
  @Output() selectExisting = new EventEmitter<void>();
  @Output() createNew = new EventEmitter<void>();
  @Output() dataSourceSelected = new EventEmitter<DataSourceGet>();

  menuOpen = false;
  listOpen = false;
  openToLeft = false;
  searchQuery = '';

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  get filteredDataSources(): DataSourceGet[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.dataSources;
    }

    return this.dataSources.filter(dataSource =>
      dataSource.dataSourceName?.toLowerCase().includes(query)
    );
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
    this.listOpen = false;

    if (this.menuOpen) {
      this.openToLeft = this.shouldOpenToLeft();
    }
  }

  onUploadFile(): void {
    this.closeMenu();
    this.uploadFile.emit();
  }

  onSelectExisting(): void {
    this.menuOpen = false;
    this.listOpen = true;
    this.searchQuery = '';
    this.selectExisting.emit();
  }

  onCreateNew(): void {
    this.closeMenu();
    this.createNew.emit();
  }

  onDataSourceSelect(dataSource: DataSourceGet): void {
    this.closeMenu();
    this.dataSourceSelected.emit(dataSource);
  }

  backToMenu(): void {
    this.listOpen = false;
    this.menuOpen = true;
    this.searchQuery = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Node && !this.elementRef.nativeElement.contains(target)) {
      this.closeMenu();
    }
  }

  isSelected(dataSource: DataSourceGet): boolean {
    if (this.allowMultipleSelection) {
      return !!dataSource.dataSourceId && this.selectedDataSourceIds.includes(dataSource.dataSourceId);
    }

    return !!this.selectedDataSourceId && dataSource.dataSourceId === this.selectedDataSourceId;
  }

  private closeMenu(): void {
    this.menuOpen = false;
    this.listOpen = false;
    this.searchQuery = '';
  }

  private shouldOpenToLeft(): boolean {
    const pickerBounds = this.elementRef.nativeElement.getBoundingClientRect();
    const dropdownWidth = 340;
    const rightMargin = 16;

    return pickerBounds.right + dropdownWidth + rightMargin > window.innerWidth;
  }
}
